/**
 * Tracking sources: a place outside our own messages where the brand shares a link or a code,
 * such as an Instagram bio or a flyer. Each source has its own tracked link and, optionally,
 * its own promo code, so clicks, orders and revenue can be told apart by source.
 */
import type { SourcePlatform, TrackingSource } from '../data/types';

export const PLATFORM_OPTIONS: { value: SourcePlatform; label: string; icon: string }[] = [
  { value: 'instagram', label: 'Instagram', icon: 'photo_camera' },
  { value: 'facebook', label: 'Facebook', icon: 'thumb_up' },
  { value: 'google', label: 'Google', icon: 'search' },
  { value: 'website', label: 'Website', icon: 'language' },
  { value: 'print', label: 'Print or QR code', icon: 'qr_code_2' },
  { value: 'other', label: 'Other', icon: 'link' },
];

export const PLATFORM_LABELS = Object.fromEntries(PLATFORM_OPTIONS.map((p) => [p.value, p.label])) as Record<SourcePlatform, string>;
export const PLATFORM_ICONS = Object.fromEntries(PLATFORM_OPTIONS.map((p) => [p.value, p.icon])) as Record<SourcePlatform, string>;

export const SOURCE_NAME_LIMIT = 60;
export const SOURCE_CODE_MIN = 4;
export const SOURCE_CODE_MAX = 15;

/** Codes are typed by customers, so they are upper case letters and digits only. */
export function normalizeCode(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, SOURCE_CODE_MAX);
}

export interface SourceForm {
  id?: string;
  name: string;
  platform: SourcePlatform;
  note: string;
  linkTarget: TrackingSource['linkTarget'];
  /** Empty when the source has no promo code. */
  promoCode: string;
  offerId: string | null;
  offerName: string | null;
}

export type SourceErrors = Partial<Record<'name' | 'promoCode' | 'offerId', string>>;

/** Name is required and unique; a code is optional, but needs an offer and must be unique. */
export function validateSource(form: SourceForm, existing: Pick<TrackingSource, 'id' | 'name' | 'promoCode'>[]): SourceErrors {
  const errors: SourceErrors = {};
  const others = existing.filter((s) => s.id !== form.id);
  const name = form.name.trim();
  if (!name) errors.name = 'Enter a name for this source';
  else if (others.some((s) => s.name.trim().toLowerCase() === name.toLowerCase())) errors.name = 'Another source already has this name';

  const code = form.promoCode.trim();
  if (code) {
    if (code !== normalizeCode(code) || code.length < SOURCE_CODE_MIN) {
      errors.promoCode = `Use ${SOURCE_CODE_MIN} to ${SOURCE_CODE_MAX} letters and numbers`;
    } else if (others.some((s) => s.promoCode === code)) {
      errors.promoCode = 'Another source already uses this code';
    }
    if (!form.offerId) errors.offerId = 'Choose the offer this code gives';
  }
  return errors;
}

/** Short link shown to the merchant; the token carries no customer or source details. */
export function sourceLink(token: string): string {
  return `brsq.co/s/${token}`;
}

/** Share of clicks that became an order, or null when there were no clicks. */
export function conversionRate(source: Pick<TrackingSource, 'clicks' | 'orders'>): number | null {
  return source.clicks > 0 ? source.orders / source.clicks : null;
}

export function toSourceForm(source: TrackingSource): SourceForm {
  return {
    id: source.id,
    name: source.name,
    platform: source.platform,
    note: source.note,
    linkTarget: source.linkTarget,
    promoCode: source.promoCode ?? '',
    offerId: source.offerId,
    offerName: source.offerName,
  };
}

export const EMPTY_SOURCE_FORM: SourceForm = {
  name: '', platform: 'instagram', note: '', linkTarget: 'online_ordering', promoCode: '', offerId: null, offerName: null,
};
